import {
  AfterViewInit,
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import { FormGroup } from "@angular/forms";
import { forkJoin, Observable, of, Subject } from "rxjs";
import { map, takeUntil } from "rxjs/operators";
import {
  addressTypeKey,
  buildingTypeKey,
  OutsideUnitBaseComponent,
  studentHomeCountKey,
  numberOfFloorsKey,
} from "src/app/core/survey/shared/components/outside-unit-base.component";
import { buildingTypeMessages } from "src/app/core/survey/shared/services/building-type-validation.messages";
import { buildingTypeValidationRules } from "src/app/core/survey/shared/services/building-type-validation.service";
import { FormValidationService } from "src/app/core/survey/shared/services/formValidation.service";
import { SavingStateService } from "src/app/core/survey/shared/services/saving-state.service";
import { SurveyFrameTabTemplateComponent } from "src/app/core/survey/shared/survey-frame-list/survey-frame-tab-template/survey-frame-tab-template.component";
import { AddressTypeEnum } from "src/app/shared/Models/enums/AddressTypeEnum";
import { UnitTypeEnum } from "src/app/shared/Models/enums/UnitUnitTypeEnum";
import { ChangeBuildingTypeDTO } from "src/app/shared/Models/write/ChangeBuildingTypeDTO";
import { ChangeOutsideUnitInfoFromInsideSurveyDTO } from "src/app/shared/Models/write/ChangeOutsideUnitInfoFromInsideSurveyDTO";
import { SaveOutsideSurveyUnitInfoAddressTypeDto } from "src/app/shared/Models/write/SaveOutsideSurveyUnitInfoDto";
import { MduService } from "src/app/shared/services/mdu.service";
import { ToastService } from "src/app/shared/toast.service";
import { BuildingTypeEnum } from "../../../../../../shared/Models/enums/BuildingTypeEnum";
import { Building } from "../../../../../../shared/Models/read";
import {
  InsideSurveyOutsideUnits,
  INSIDE_SURVEY_OUTSIDE_UNITS,
} from "../../../config/inside-survey.magic-strings";
import { InsideSurveyTab } from "../../../models/insideSurveyTab.interface";
import { InsideUnit } from "../../../models/insideUnit.model";
import { UnitSynchronizationService } from "../../../services/unitSynchronization.service";
import { ValidationError } from "../../../../../../shared/Models/ValidationError";
import { PermissionsService } from "src/app/shared/services/permissions.service";
import { PermissionsEnum } from "src/app/shared/Models/enums/PermissionsEnum";

const buildingFields = [
  "numberOfFloors",
  "numberOfLivingUnits",
  "numberOfBusinessUnits",
  "numberOfServiceUnits",
  "studentHomeCount",
  "buildingType",
];

const addressKey = "address";
const buildingKey = "building";

@Component({
  selector: "app-inside-survey-outside-units",
  templateUrl: "./inside-survey-outside-units.component.html",
  styleUrls: ["./inside-survey-outside-units.component.scss"],
})
export class InsideSurveyOutsideUnitsComponent
  extends OutsideUnitBaseComponent
  implements InsideSurveyTab, OnInit, OnDestroy, AfterViewInit
{
  tabId: string = INSIDE_SURVEY_OUTSIDE_UNITS;
  @Input() buildings: Building[];
  @Input() insideUnits: InsideUnit[][];
  @Input() isAuthCanWrite: boolean;
  @ViewChild("tabs") tabsComponent: SurveyFrameTabTemplateComponent;
  selectedBuildingType: BuildingTypeEnum = null;
  check = true;
  insideSurveyOutsideUnits = InsideSurveyOutsideUnits;
  buildingTypeOptions: any[]; // workaround for dropdown dblclk
  addressTypeOptions: any[]; // https://github.com/primefaces/primeng/issues/2358
  constructor(
    private unitSynchronizationService: UnitSynchronizationService,
    private _toastService: ToastService,
    private _savingStateService: SavingStateService,
    private _mduService: MduService,
    private formValidationService: FormValidationService,
    private permissionsService: PermissionsService
  ) {
    super();
  }
  buildingTypeForm: { [key: string]: FormGroup } = {};
  debouncer: Subject<ChangeBuildingTypeDTO> =
    new Subject<ChangeBuildingTypeDTO>();

  get contexts() {
    return this.buildings.map((b, i) => ({ id: b.id, index: i, building: b }));
  }

  ngOnInit() {
    this.buildings.forEach((b) => {
      this.formValidationService.addFormError(
        [INSIDE_SURVEY_OUTSIDE_UNITS, b.id],
        {
          [InsideSurveyOutsideUnits.FLOORS_FIELD]: false,
          [InsideSurveyOutsideUnits.BUILDING_CHARACTERISTIC_FIELD]: false,
          [InsideSurveyOutsideUnits.UNITS_FIELD]: false,
          [InsideSurveyOutsideUnits.NUMBER_OF_ROOMS_FIELD]: false,
        }
      );
      b.addresses.forEach((a) => {
        this.formValidationService.addFormError(
          [INSIDE_SURVEY_OUTSIDE_UNITS, b.id, a.id],
          {
            [InsideSurveyOutsideUnits.NUMBER_OF_ROOMS_FIELD]: false,
          }
        );
      });
    });

    this.init(this.buildings);
    this.buildForms();

    var initialBuilding = this.buildings[0];

    if (initialBuilding) {
      this.setInitialValues(initialBuilding);
      this.setAdressTypePossibilities(
        initialBuilding,
        this.unitForms[initialBuilding.id].get(buildingTypeKey).value
      );
      this.buildingTypesDuringInsideSurvey();
    }

    this.unitSynchronizationService.insideUnitChanged
      .pipe(takeUntil(this.destroy$))
      .subscribe(([buildingId, type, amount]: [string, UnitTypeEnum, number]) =>
        this.patchForm(buildingId, type, amount)
      );

    Object.keys(this.unitForms).forEach((s) => {
      this.unitForms[s].valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe((res) => {
          var building = this.buildings.find((b) => b.id == s);
          var mdu = this._mduService.isMdu(
            res.numberOfLivingUnits,
            res.numberOfBusinessUnits,
            res.numberOfServiceUnits
          );
          this.unitForms[s]
            .get("checkbox")
            .patchValue(mdu, { emitEvent: false });
          res.checkbox = mdu;
          this.mapUpdateUnitFormToBuilding(res, building);
        });
    });
  }

  ngAfterViewInit(): void {
    this.tabsComponent?.onToggleDetail(this.buildings[0]?.id);
  }

  buildingTypesDuringInsideSurvey() {
    this.buildingTypeOptions = Object.keys(BuildingTypeEnum)
      .filter(
        (key) =>
          !isNaN(+key) &&
          key != `${BuildingTypeEnum.NotInUse}` &&
          key != `${BuildingTypeEnum.NoAccess}` &&
          key != `${BuildingTypeEnum.EmptyParcel}`
      )
      .map((key) => ({
        value: +key,
        label: BuildingTypeEnum[key],
      }));
  }

  addressTypesForBuilding(b: Building) {
    this.addressTypeOptions = this.addressTypeTexts[b.id].map((a) => ({
      label: AddressTypeEnum[a],
      value: a,
    }));
  }

  private mapUpdateUnitFormToBuilding(res: any, building: Building) {
    building.buildingType = res.buildingType;
    building.studentHomeCount = res.buildingstudentHomeCount;
    building.outsideSurvey.numberOfBusinessUnits = res.numberOfBusinessUnits;
    building.outsideSurvey.numberOfLivingUnits = res.numberOfLivingUnits;
    building.outsideSurvey.numberOfServiceUnits = res.numberOfServiceUnits;
    building.outsideSurvey.numberOfFloors = res.numberOfFloors;
    building.outsideSurvey.isMDU = res.checkbox;

    building.addresses.map((addr, i) => {
      let term = `addressType${i}`;
      addr.addressType = res[term];
      term = `${addressKey}${addr.id}${studentHomeCountKey}`;
      addr.studentHomeCount = res[term];
    });
  }

  private buildForms() {
    this.buildings.forEach((b) => {
      this.unitForms[b.id].patchValue(
        {
          ...b.outsideSurvey,
          checkbox: this._mduService.isMdu(
            b.outsideSurvey.numberOfLivingUnits,
            b.outsideSurvey.numberOfBusinessUnits,
            b.outsideSurvey.numberOfServiceUnits
          ),
        },
        { emitEvent: false }
      );
    });
  }

  private setInitialValues(building: Building) {
    this.unitQuestions.map((u) => {
      if (building.outsideSurvey.hasOwnProperty(u.key)) {
        u.quantity =
          building.outsideSurvey[u.key] === null
            ? 0
            : building.outsideSurvey[u.key];
        u.originalValue = building[u.key] === null ? 0 : building[u.key];
      }
      return u;
    });
  }

  get isValidator() {
    return this.permissionsService.allowed(PermissionsEnum.ValidateSurvey);
  }

  private patchForm(buildingId: string, type: UnitTypeEnum, amount: number) {
    const building = this.buildings.find((b) => b.id == buildingId);
    switch (type) {
      case UnitTypeEnum.serviceUnit:
        building.outsideSurvey.numberOfServiceUnits = amount;
        break;
      case UnitTypeEnum.livingUnit:
        building.outsideSurvey.numberOfLivingUnits = amount;
        break;
      case UnitTypeEnum.businessUnit:
        building.outsideSurvey.numberOfBusinessUnits = amount;
        break;
    }
    this.unitForms[buildingId].patchValue({ ...building.outsideSurvey });
  }

  onTabOpenend() {
    this.buildings.forEach((building) => {
      this.addressTypesForBuilding(building);
    });
  }

  onQuantityChange(
    newQuantity: number,
    questionKey: string,
    buildingId: string
  ) {
    if (newQuantity > 1000 || newQuantity < 0) return;

    const unitQuestion = this.unitQuestions.find((u) => u.key === questionKey);
    if (unitQuestion) unitQuestion.quantity = newQuantity;

    const floorQuestion = this.floorQuestions.find(
      (u) => u.key === questionKey
    );
    if (floorQuestion) floorQuestion.quantity = newQuantity;

    if (this.unitQuestions.every((u) => u.quantity !== null))
      this.setAdressTypePossibilities(
        this.buildings.find((b) => b.id == buildingId),
        this.unitForms[buildingId].get(buildingTypeKey).value
      );
  }

  getErrors(building?: string, address?: string, field?: string) {
    return this.formValidationService.getErrors(
      ...[INSIDE_SURVEY_OUTSIDE_UNITS, building, address, field].filter(
        (x) => !!x
      )
    );
  }

  getValidationError(checkValidity?: boolean): ValidationError {
    if (checkValidity)
      this.formValidationService.resetErrorsForPath(
        INSIDE_SURVEY_OUTSIDE_UNITS
      );
    var valError;
    for (let building of this.buildings) {
      if (
        !this.unitForms[building.id].get(buildingTypeKey) ||
        this.unitForms[building.id].get(buildingTypeKey).value == "-1" ||
        building.buildingType == null
      ) {
        if (checkValidity)
          this.formValidationService.addError(
            INSIDE_SURVEY_OUTSIDE_UNITS,
            building.id,
            InsideSurveyOutsideUnits.BUILDING_CHARACTERISTIC_FIELD
          );

        valError = new ValidationError(
          $localize`:@@general.warning:Warning`,
          $localize`:@@validation.survey.address.selecttype:Building type is required!`,
          $localize`:@@survey.inside.outside.units.title:Inside Survey, Outside Units`
        );
      }

      if (this.unitForms[building.id].get(numberOfFloorsKey).value <= 0) {
        if (checkValidity)
          this.formValidationService.addError(
            INSIDE_SURVEY_OUTSIDE_UNITS,
            building.id,
            InsideSurveyOutsideUnits.FLOORS_FIELD
          );

        valError = new ValidationError(
          $localize`:@@general.warning:Warning`,
          $localize`:@@validation.survey.building.atleastonefloor:Building requires at least 1 floor.`,
          $localize`:@@survey.inside.outside.units.title:Inside Survey, Outside Units`
        );
      }

      if (
        this.unitForms[building.id].get(buildingTypeKey).value ==
          BuildingTypeEnum.EmptyParcel ||
        this.unitForms[building.id].get(buildingTypeKey).value ==
          BuildingTypeEnum.NoAccess ||
        this.unitForms[building.id].get(buildingTypeKey).value ==
          BuildingTypeEnum.NotInUse
      ) {
        if (checkValidity)
          this.formValidationService.addError(
            INSIDE_SURVEY_OUTSIDE_UNITS,
            building.id,
            InsideSurveyOutsideUnits.BUILDING_CHARACTERISTIC_FIELD
          );

        valError = new ValidationError(
          $localize`:@@general.warning:Warning`,
          $localize`:@@validation.survey.address.selecttype:NoAccess, NotInUse and EmptyParcel are invalid building types for an inside survey!`,
          $localize`:@@survey.inside.outside.units.title:Inside Survey, Outside Units`
        );
      }

      if (
        building.buildingType == this.buildingTypeEnum.InUse &&
        building.addresses.every(
          (a) => a.addressType == AddressTypeEnum.NotInUse
        )
      ) {
        valError = new ValidationError(
          $localize`:@@general.warning:Warning`,
          $localize`:@@validation.survey.building.atLeastOneNotNotInUse:At least one address should be different from Not In Use for a building of type In Use!`,
          $localize`:@@survey.inside.outside.units.title:Inside Survey, Outside Units`
        );
        break;
      }

      building.addresses
        .filter((a) => a.addressType == AddressTypeEnum.StudentHome)
        .forEach((a) => {
          a.studentHomeCount = this.unitForms[building.id].get(
            addressKey + a.id + studentHomeCountKey
          )?.value;

          if (building.outsideSurvey.numberOfBusinessUnits < 1) {
            if (checkValidity)
              this.formValidationService.addError(
                INSIDE_SURVEY_OUTSIDE_UNITS,
                building.id,
                InsideSurveyOutsideUnits.UNITS_FIELD
              );

            valError = buildingTypeMessages.studentHomeAtleastOneBU;
          }

          if (a.studentHomeCount < 1) {
            if (checkValidity)
              this.formValidationService.addError(
                INSIDE_SURVEY_OUTSIDE_UNITS,
                building.id,
                a.id,
                InsideSurveyOutsideUnits.NUMBER_OF_ROOMS_FIELD
              );

            valError = buildingTypeMessages.studentHomeAtleastOneStudentHome;
          }
        });

      if (
        building.addresses.some(
          (a) => a.addressType == this.addressTypeEnum.EmptyParcel
        ) &&
        building.buildingType != this.buildingTypeEnum.EmptyParcel
      ) {
        if (checkValidity)
          this.formValidationService.addError(
            INSIDE_SURVEY_OUTSIDE_UNITS,
            building.id,
            InsideSurveyOutsideUnits.BUILDING_CHARACTERISTIC_FIELD
          );

        valError = new ValidationError(
          $localize`:@@general.warning: Warning`,
          $localize`:@@validation.survey.emptyParcelAddressButNotBuilding: An address can only be of type Empty Parcel if the Building is of that same type.`,
          $localize`:@@survey.inside.outside.units.title:Inside Survey, Outside Units`
        );
      }

      if (
        building.addresses.some(
          (a) => a.addressType == this.addressTypeEnum.NoAccess
        )
      ) {
        if (checkValidity)
          this.formValidationService.addError(
            INSIDE_SURVEY_OUTSIDE_UNITS,
            building.id,
            InsideSurveyOutsideUnits.BUILDING_CHARACTERISTIC_FIELD
          );

        valError = new ValidationError(
          $localize`:@@general.warning: Warning`,
          $localize`:@@validation.survey.NoAccessAddressButNotBuilding: An address cannot be of type No Access.`,
          $localize`:@@survey.inside.outside.units.title:Inside Survey, Outside Units`
        );
      }

      if (
        building.addresses.some((address, index) => {
          return (
            this.unitForms[building.id].get(addressTypeKey + index).value == -1
          );
        })
      )
        valError = new ValidationError(
          $localize`:@@general.warning:Warning`,
          $localize`:@@validation.survey.address.selecttype:Address type is required!`,
          $localize`:@@survey.inside.outside.units.title:Inside Survey, Outside Units`
        );

      let buidingTypeValidation: boolean | ValidationError =
        buildingTypeValidationRules[
          this.unitForms[building.id].get(buildingTypeKey).value
        ](building, this.calculateTotalUnitsForBuilding(building));
      if (typeof buidingTypeValidation == "object") {
        if (checkValidity)
          this.formValidationService.addError(
            INSIDE_SURVEY_OUTSIDE_UNITS,
            building.id,
            InsideSurveyOutsideUnits.BUILDING_CHARACTERISTIC_FIELD
          );

        valError = buidingTypeValidation;
      }
    }
    return valError;
  }

  validateMDuFlag(b: Building) {
    b.outsideSurvey.isMDU = this._mduService.isMdu(
      b.outsideSurvey.numberOfLivingUnits,
      b.outsideSurvey.numberOfBusinessUnits,
      b.outsideSurvey.numberOfServiceUnits
    );
    return b.outsideSurvey.isMDU;
  }

  save(checkValidity?: boolean): Observable<boolean> {
    if (checkValidity) {
      const error = this.getValidationError();
      if (error) {
        this._toastService.showWarnToast(error.title, error.message);
        return of(false);
      }
    }
    if (!this.detectChanges()) return of(true);

    if (this.isAuthCanWrite) {
      Object.values(this.unitForms).forEach((f) => f.markAsPristine());
      return forkJoin(
        this.buildings.map((thisBuilding) => {
          thisBuilding.addresses.forEach((address, index) => {
            address.addressType = this.unitForms[thisBuilding.id].get(
              addressTypeKey + index
            ).value;
          });
          thisBuilding.buildFloors();
          var dto: ChangeOutsideUnitInfoFromInsideSurveyDTO = {
            surveyId: thisBuilding.outsideSurvey.id,
            buildingId: thisBuilding.id,
            numberOfFloors: thisBuilding.outsideSurvey.numberOfFloors ?? 0,
            numberOfLivingUnits:
              thisBuilding.outsideSurvey.numberOfLivingUnits ?? 0,
            numberOfServiceUnits:
              thisBuilding.outsideSurvey.numberOfServiceUnits ?? 0,
            studentHomeCount: this.unitForms[thisBuilding.id]?.get(
              buildingKey + studentHomeCountKey
            )
              ? this.unitForms[thisBuilding.id].get(
                  buildingKey + studentHomeCountKey
                ).value
              : null,
            numberOfBusinessUnits:
              thisBuilding.outsideSurvey.numberOfBusinessUnits,
            //To be added in the Command first.
            changeAddressTypes: thisBuilding.addresses.map(
              (s) =>
                <SaveOutsideSurveyUnitInfoAddressTypeDto>{
                  addressId: s.id,
                  addressType: +s.addressType,
                  studentHomeCount:
                    this.unitForms[thisBuilding.id].get(
                      addressKey + s.id + studentHomeCountKey
                    )?.value ?? null,
                }
            ),
            buildingType: +thisBuilding.buildingType,
          };
          return this._savingStateService.addInsideSurveyOutsideUnits(dto);
        })
      ).pipe(map((val) => val.every((v) => !!v)));
    }
  }

  detectChanges() {
    return Object.values(this.unitForms).some((f: FormGroup) => f.dirty);
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  componentReload = false;
  forceReloadTab() {
    this.componentReload = false;
    this.unitQuestions.forEach((u) => (u.quantity = null));
    setTimeout(() => (this.componentReload = true), 0);
  }
}
